import React, { useEffect, useState } from "react";
import { api } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { toast } from "sonner";
import { PaperPlaneTilt, Clock } from "@phosphor-icons/react";

function SectionTable({ section }) {
  return (
    <div className="mt-4">
      <div className="flex items-baseline gap-2">
        <h4 className="text-sm font-semibold text-gray-900">{section.title}</h4>
        <span className="text-xs font-medium text-[#1B4383]">({section.rows.length})</span>
      </div>
      <p className="text-xs text-gray-400">{section.note}</p>
      {section.rows.length === 0 ? (
        <p className="mt-2 text-xs text-[#122E5C]">All caught up — nothing outstanding.</p>
      ) : (
        <div className="mt-2 overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="lx-label border-b border-gray-200">
                <th className="px-3 py-2 text-left">Task</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Assigned To</th>
                <th className="px-3 py-2 text-left">Assigned By</th>
                <th className="px-3 py-2 text-left">Due</th>
                <th className="px-3 py-2 text-right">Age</th>
              </tr>
            </thead>
            <tbody>
              {section.rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-800">{r.details}</td>
                  <td className="px-3 py-2 text-gray-500">{r.category || "—"}</td>
                  <td className="px-3 py-2 text-gray-500">{r.assigned_to_name}</td>
                  <td className="px-3 py-2 text-gray-500">{r.created_by_name}</td>
                  <td className="px-3 py-2 text-gray-500">{r.due_date || "—"}</td>
                  <td className={`px-3 py-2 text-right lx-num ${r.age_days > 7 ? "font-semibold text-red-600" : "text-gray-500"}`}>{r.age_days}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TaskEmails() {
  const [recipients, setRecipients] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/reports/daily-tasks/preview")
      .then((r) => setRecipients(r.data.recipients))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load preview"));
  };
  useEffect(load, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/daily-tasks/send-now");
      const failed = data.results.filter((r) => !r.ok);
      if (failed.length === 0) toast.success(`Sent ${data.sent}/${data.total} task summary emails`);
      else toast.warning(`Sent ${data.sent}/${data.total}. Failed: ${failed.map((f) => f.name).join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send emails");
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Daily Task Emails"
        subtitle="Automated task summaries sent to each user"
        actions={
          <Button data-testid="send-task-emails" onClick={sendNow} disabled={sending || error}
            className="bg-[#1B4383] text-white hover:bg-[#122E5C]">
            <PaperPlaneTilt size={16} className="mr-1" />{sending ? "Sending…" : "Send now"}
          </Button>
        }
      />
      <div className="p-8">
        <div className="mb-6 flex items-center gap-2 rounded-sm border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          <Clock size={18} className="text-[#122E5C]" />
          Emails are sent automatically every day at <strong className="mx-1">7:00 PM (East Africa Time)</strong>. Use "Send now" to preview-send immediately.
        </div>

        {error && <div data-testid="task-emails-error" className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {recipients && (
          <div className="space-y-6" data-testid="task-emails-preview">
            {recipients.map((rec) => (
              <div key={rec.user_id} className="rounded-sm border border-gray-200 bg-white p-5">
                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                  <div>
                    <div className="font-display text-base font-bold text-gray-900">{rec.name}</div>
                    <div className="text-xs text-gray-500">{rec.email} · {rec.role}</div>
                  </div>
                </div>
                {rec.sections.map((s, i) => <SectionTable key={i} section={s} />)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
